import { useState } from 'react';
import { getOrderFile } from '../../../api/ordersApi.js';
import StatusBadge from './StatusBadge.jsx';
import Spinner from './Spinner.jsx';
import WarningIcon from './WarningIcon.jsx';
import Tooltip from './Tooltip.jsx';
import { formatCurrency, formatDate } from '../utils/format.js';
import { classifyPortalError } from '../utils/apiError.js';
import { showErrorToast, showSuccessToast } from '../utils/toast.js';
import './OrderDetailPanel.css';

const TABS = ['Order Details', 'Products', 'Customer Info', 'Timeline', 'PO File', 'Documents', 'Comments'];

function formatAddress(address) {
  if (!address) return '—';
  return [address.line1, address.street, address.landmark, address.city, address.state, address.postalCode, address.country]
    .filter(Boolean).join(', ') || '—';
}

function formatDateTime(dateString) {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function OrderDetailPanel({ order, detail, loading, error, errorMessage, manualRetries, onRetry }) {
  const [activeTab, setActiveTab] = useState('Order Details');

  if (loading) {
    return (
      <div className="customer-portal-order-detail">
        <div className="customer-portal-order-detail__loader">
          <Spinner size={28} />
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="customer-portal-order-detail">
        <div className="customer-portal-card__error">
          <WarningIcon />
          <span>{errorMessage || "Couldn't load order details"}</span>
          {manualRetries > 0 && (
            <button
              onClick={onRetry}
              className="customer-portal-btn customer-portal-btn--primary customer-portal-btn--sm"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  const handleFileDownload = async (fileName, isPOFile = false) => {
    const orderId = detail.id ?? order.id;
    if (!orderId || !fileName) return;
    try {
      const res = await getOrderFile(orderId, fileName, isPOFile);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers?.['content-type'] || 'application/octet-stream' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccessToast(`Downloaded ${fileName}`);
    } catch (err) {
      showErrorToast(classifyPortalError(err).message || "Couldn't download the file. Please try again.");
    }
  };

  const products = Array.isArray(detail.products) ? detail.products : [];
  const customer = detail.customer || {};
  const deliveryContact = detail.deliveryContact || {};
  const deliveryAddress = detail.deliveryAddress || {};
  const comments = Array.isArray(detail.comments) ? detail.comments : [];

  const poFiles = detail.purchaseOrderFileName
    ? [{ fileName: detail.purchaseOrderFileName, isPOFile: true }]
    : [];

  const additionalFiles = Array.isArray(detail.additionalFiles)
    ? detail.additionalFiles.map((f) => (typeof f === 'string' ? { fileName: f } : f))
    : [];

  return (
    <div className="customer-portal-order-detail">
      <div className="customer-portal-order-detail__tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`customer-portal-order-detail__tab${activeTab === tab ? ' customer-portal-order-detail__tab--active' : ''}`}
            onClick={() => setActiveTab(tab)}
            type="button"
          >
            {tab}
            {tab === 'Products' && <span className="customer-portal-order-detail__tab-count">{products.length}</span>}
            {tab === 'PO File' && <span className="customer-portal-order-detail__tab-count customer-portal-order-detail__tab-count--muted">{poFiles.length}</span>}
            {tab === 'Documents' && (
              <span className="customer-portal-order-detail__tab-count customer-portal-order-detail__tab-count--muted">{additionalFiles.length}</span>
            )}
          </button>
        ))}
        {detail.requiredDate && (
          <div className="customer-portal-order-detail__required-date">
            <span>Required Date</span>
            <strong>{formatDate(detail.requiredDate)}</strong>
          </div>
        )}
      </div>

      <div className="customer-portal-order-detail__content">
        {activeTab === 'Order Details' && (
          <div className="customer-portal-order-detail__grid">
            <Field label="Purchase Order ID" value={detail.purchaseOrderId} />
            <Field label="Sales Order Group" value={detail.salesOrderGroup} />
            <Field label="Sales Person" value={detail.salesPerson?.name || detail.salesPerson} />
            <Field label="Payment Term" value={detail.paymentTerm?.name || detail.paymentTerm} />
            <Field label="Warehouse" value={detail.warehouseName || detail.warehouse?.name} />
            <Field label="Delivery Instruction" value={detail.deliveryInstruction} />
            <div className="customer-portal-order-detail__field">
              <div className="customer-portal-order-detail__field-label">Status</div>
              <StatusBadge status={detail.status || order.status} />
            </div>
            <Field label="Total Amount" value={formatCurrency(detail.total ?? order.total, detail.currency || order.currency)} strong />
          </div>
        )}

        {activeTab === 'Products' && (
          products.length === 0 ? (
            <div className="customer-portal-order-detail__empty-state">No products available</div>
          ) : (
          <table className="customer-portal-order-detail__table">
            <colgroup>
              <col style={{ width: '42%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '20%' }} />
            </colgroup>
            <thead>
              <tr>
                <th className="customer-portal-orders-full-table__center">Product</th>
                <th className="customer-portal-orders-full-table__center">Qty</th>
                <th className="customer-portal-orders-full-table__right">Customer Price</th>
                <th className="customer-portal-orders-full-table__right">Total</th>
                <th className="customer-portal-orders-full-table__center">Comments</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => {
                  const qty = Number(p.quantity ?? 0);
                  const customerPrice = Number(p.customerPrice ?? 0);
                  const lineTotal = Number(p.total ?? customerPrice * qty);
                  const hasComment = Boolean(p.comments?.trim());
                  return (
                    <tr key={p.id}>
                      <td className="customer-portal-orders-full-table__center">
                        <Tooltip as="div" className="customer-portal-order-detail__product-name">{p.productCode || '—'}</Tooltip>
                        <Tooltip as="div" className="customer-portal-order-detail__product-desc">{p.description || '—'}</Tooltip>
                      </td>
                      <Tooltip as="td" className="customer-portal-orders-full-table__center">{qty}</Tooltip>
                      <Tooltip as="td" className="customer-portal-orders-full-table__right">{formatCurrency(customerPrice, detail.currency || order.currency)}</Tooltip>
                      <Tooltip as="td" className="customer-portal-orders-full-table__right">{formatCurrency(lineTotal, detail.currency || order.currency)}</Tooltip>
                      <td className="customer-portal-orders-full-table__center">
                        {hasComment ? (
                          <Tooltip
                            as="span"
                            className="customer-portal-order-detail__comment-icon customer-portal-order-detail__comment-icon--active"
                            content={p.comments}
                            forceOverflow
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                            </svg>
                          </Tooltip>
                        ) : (
                          <span className="customer-portal-order-detail__comment-icon customer-portal-order-detail__comment-icon--inactive">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                            </svg>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
          )
        )}

        {activeTab === 'Customer Info' && (
          <div className="customer-portal-order-detail__grid">
            <Field label="Customer Name" value={customer.name} />
            <Field label="Customer Entity" value={customer.entity || customer.company} />
            <Field label="Customer Code" value={customer.customerCode} />
            <Field label="Delivery Contact" value={deliveryContact.name} />
            <Field label="Delivery Email" value={deliveryContact.email || detail.email} />
            <Field label="Phone No" value={deliveryContact.phoneNo || detail.phoneNo} />
            <Field label="Delivery Address" value={formatAddress(deliveryAddress)} wide />
          </div>
        )}

        {activeTab === 'Timeline' && (
          <div className="customer-portal-order-detail__grid">
            <Field label="Order Date" value={formatDateTime(detail.createdAt)} />
            <Field label="Purchase Order Date" value={formatDate(detail.purchaseOrderDate)} />
            {detail.requiredDate && <Field label="Required Date" value={formatDate(detail.requiredDate)} />}
            <Field label="Last Updated" value={formatDateTime(detail.updatedAt)} />
          </div>
        )}

        {activeTab === 'PO File' && (
          <div className="customer-portal-order-detail__empty-state">
            {poFiles.length === 0 ? (
              <div>No purchase order file uploaded.</div>
            ) : (
              poFiles.map((f, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="customer-portal-order-detail__file-row"
                  onClick={() => handleFileDownload(f.fileName, f.isPOFile)}
                >
                  <span className="customer-portal-order-detail__file-icon">📄</span>
                  <Tooltip as="span" className="customer-portal-order-detail__file-name">{f.fileName}</Tooltip>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="customer-portal-order-detail__file-dl-icon">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </button>
              ))
            )}
          </div>
        )}

        {activeTab === 'Documents' && (
          <div className="customer-portal-order-detail__empty-state">
            {additionalFiles.length === 0 ? (
              <div>No additional documents uploaded.</div>
            ) : (
              additionalFiles.map((f, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="customer-portal-order-detail__file-row"
                  onClick={() => handleFileDownload(f.fileName)}
                >
                  <span className="customer-portal-order-detail__file-icon">📎</span>
                  <Tooltip as="span" className="customer-portal-order-detail__file-name">{f.fileName}</Tooltip>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="customer-portal-order-detail__file-dl-icon">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </button>
              ))
            )}
          </div>
        )}

        {activeTab === 'Comments' && (() => {
          const hasConfig = Boolean(detail.config?.trim());
          if (!hasConfig && comments.length === 0) {
            return <div className="customer-portal-order-detail__empty-state">No comments found.</div>;
          }
          return (
            <div className="customer-portal-order-detail__comments-list">
              {hasConfig && (
                <div className="customer-portal-order-detail__comment-item customer-portal-order-detail__comment-item--config">
                  <div className="customer-portal-order-detail__comment-type">Additional Comment</div>
                  <div className="customer-portal-order-detail__comment-message">{detail.config}</div>
                </div>
              )}
              {comments.map((c, idx) => {
                const type = String(c.type || '').trim();
                const typeClass = type.toLowerCase() === 'approved'
                  ? 'customer-portal-order-detail__comment-item--approved'
                  : type.toLowerCase() === 'rejected'
                    ? 'customer-portal-order-detail__comment-item--rejected'
                    : 'customer-portal-order-detail__comment-item--neutral';
                return (
                  <div key={c.id || idx} className={`customer-portal-order-detail__comment-item ${typeClass}`}>
                    <div className="customer-portal-order-detail__comment-header">
                      <span className="customer-portal-order-detail__comment-type">{type || '-'}</span>
                      {c.createdAt && (
                        <span className="customer-portal-order-detail__comment-date">{formatDate(c.createdAt)}</span>
                      )}
                    </div>
                    <div className="customer-portal-order-detail__comment-message">{c.message || '-'}</div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function Field({ label, value, strong, wide }) {
  return (
    <div className={`customer-portal-order-detail__field${wide ? ' customer-portal-order-detail__field--wide' : ''}`}>
      <div className="customer-portal-order-detail__field-label">{label}</div>
      <div className={`customer-portal-order-detail__field-value${strong ? ' customer-portal-order-detail__field-value--strong' : ''}`}>
        {value ?? '—'}
      </div>
    </div>
  );
}
